import React, { useState, useEffect, useRef } from "react";
import "./index.scss";
import Toogle from "../../dynamic/Toggle";
import Pager, { pagerListener } from "../Pager/Pager";
import Inductee from "./Inductee";
import MobilePager, { pagerOptions } from "./dropdownPager";
import { Person } from "./types";
import { useDebounce, debounce, getMediaDevice } from "../../helpers";
import { dropdownItem } from "../Dropdown/types";
import Loading from "../Loading/Loading";

const HallOfFame = ({
  people,
  title = ""
}: {
  people: Person[];
  title?: string;
}) => {
  const inductee: Person = {
    imageUrl: "",
    firstName: "",
    lastName: "",
    category: "Player",
    classYear: 0,
    nationalityCode: "",
    profileUrl: "",
    articleUrl: "",
    achievements: [],
    quote: "",
    searchable: ""
  };
  const Module = useRef<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState({
    key: 0,
    inductee
  });
  const expandedCardRef = useRef<HTMLDivElement | null>(null);
  const [toggles, updateToggle] = useState([
    {
      name: "Players",
      value: "Player",
      key: 0,
      active: true
    },
    {
      name: "Contributors",
      value: "Contributor",
      key: 1,
      active: false
    }
  ]);
  const [toggleExpand, updateToggleExpand] = useState(false);
  const [pagerOffset, setPagerOffset] = useState<number>(0);
  const initInducteeState = people.reduce((persons: Person[], person, key) => {
    if (!person.category) {
      const inductee = [
        {
          ...person,
          category: "Contributor",
          key,
          searchable: `${person.firstName.toLowerCase()} ${person.lastName.toLowerCase()} ${
            person.classYear
          } ${person.nationalityCode}`
        },
        {
          ...person,
          category: "Player",
          key,
          searchable: `${person.firstName.toLowerCase()} ${person.lastName.toLowerCase()} ${
            person.classYear
          } ${person.nationalityCode}`
        }
      ];
      persons.push(...inductee);
    } else {
      persons.push({
        ...person,
        key,
        searchable: `${person.firstName.toLowerCase()} ${person.lastName.toLowerCase()} ${
          person.classYear
        } ${person.nationalityCode}`
      });
    }

    return persons;
  }, []);
  const [inducteeFilter, filterInductees] = useState<Person[]>([]);

  const [moduleLoad, updateModuleLoad] = useState(false);

  const func = () => {
    if (Module && Module.current && !moduleLoad) {
      const flag =
        Math.ceil(Module.current.getBoundingClientRect().top) -
          Module.current.clientHeight <
          0 || getMediaDevice() == "MOBILE";
      const isLoaded = Module.current.getAttribute("loaded");
      if (flag && !isLoaded) {
        Module.current.setAttribute("loaded", "true");
        updateModuleLoad(true);
        const inductees = initInducteeState.filter(
          person => person.category === toggles.filter(t => t.active)[0].value
        );
        const remainder = inductees.length - parseInt(pagerOptions[0].label);
        const remaining =
          inductees.length > parseInt(pagerOptions[0].label) &&
          remainder > parseInt(pagerOptions[0].label)
            ? parseInt(pagerOptions[0].label)
            : inductees.length - parseInt(pagerOptions[0].label);
        updateMobilePager({ ...mobilePager, remaining });
        filterInductees(inductees);
      }
    }
  };

  useEffect(() => {
    debounce({ event: "scroll", func, timeOut: null });
  }),
    [];

  const [timeout, updateTimeout] = useState(0);
  const [searchString, updateSearch] = useState({ value: "" });
  const onKeyPressHandler = (value: string) => {
    updateToggleExpand(false);
    setPagerOffset(0);
    if (value === "") {
      setSelected({ key: 0, inductee });
      filterInductees(
        initInducteeState.filter(
          person => person.category === toggles.filter(t => t.active)[0].value
        )
      );
    } else {
      filterInductees(
        initInducteeState
          .filter(
            person => person.category === toggles.filter(t => t.active)[0].value
          )
          .filter(
            person =>
              person.searchable &&
              person.searchable.indexOf(value.toLowerCase()) > -1
          )
      );
    }
    updateSearch({ value });
  };

  const activeToggle = toggles.filter(t => t.active)[0].name;

  const updateToggleHandler = (value: string) => {
    const updatedToggleState = toggles.map(tog => ({
      ...tog,
      active: value === tog.value
    }));
    const category = toggles.filter(op => op.value == value)[0];
    const updatedInductees = initInducteeState
      .filter(person => person.category == category.value)
      .filter(
        person =>
          person.searchable &&
          person.searchable.indexOf(searchString.value.toLowerCase()) > -1
      );
    updateToggle(updatedToggleState);
    filterInductees(updatedInductees);
    if (updatedInductees.length) {
      setSelected({
        key: 0,
        inductee: updatedInductees[0]
      });
    }
  };

  const mouseUpHandler = (value: string) => {
    const category = toggles.filter(op => op.active)[0];
    if (value !== category.value) {
      filterInductees([]);
      setPagerOffset(0);
    }
  };

  const searchFieldOnChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    useDebounce({
      timeout,
      updateTimeout,
      delay: 500,
      func: onKeyPressHandler,
      params: [e.target.value]
    });
  };

  const pagerHookHandler = ({
    tilesWrapperWidth,
    tileWidth,
    tilePadding,
    handlePrev,
    handleNext,
    offsetValue,
    excess,
    screenResize = false
  }: {
    tilesWrapperWidth: number;
    offsetValue: number;
    handleNext: (disableEmitter?: boolean) => void;
    handlePrev: (disableEmitter?: boolean) => void;
    tileWidth: number;
    tilePadding: number;
    excess: number;
    screenResize?: boolean;
  }) => {
    const key: number = Math.floor(
      tilesWrapperWidth / (tileWidth + tilePadding) + offsetValue
    );
    const nextFlag = selected.key == key;
    const prevFlag = !!(excess && selected.key == Math.floor(offsetValue));
    if (screenResize) {
      const isToggleExanded =
        Module.current && Module.current.getAttribute("expanded") == "true";
      setPagerOffset(0);
      if (isToggleExanded && getMediaDevice() !== "MOBILE") {
        updateToggleExpand(false);
      }
    } else if (nextFlag) {
      handleNext(true);
      setPagerOffset(pagerOffset + 1);
    } else if (prevFlag) {
      handlePrev(true);
      setPagerOffset(pagerOffset > 0 ? pagerOffset - 1 : pagerOffset);
    }
  };

  useEffect(() => {
    const subscribe = pagerListener({
      callback: ({
        dir,
        offsetValue,
        data
      }: {
        dir: "next" | "prev";
        offsetValue: number;
        data: {
          selected: { key: number; inductee: Person[] };
          inducteeFilter: Person[];
          pagerOffset: number;
        };
      }) => {
        const { selected, inducteeFilter } = data;
        const nextSelectedKey = selected.key + 1;
        const prevSelectedKey = selected.key - 1;
        const nextKey =
          nextSelectedKey < inducteeFilter.length ? nextSelectedKey : 0;
        const prevKey =
          prevSelectedKey < 0 ? inducteeFilter.length - 1 : prevSelectedKey;
        const key = dir == "next" ? nextKey : prevKey;
        const condition =
          (dir == "next" && selected.key < inducteeFilter.length - 1) ||
          (dir == "prev" && selected.key > 0);
        const newOffset = dir == "prev" ? offsetValue - 1 : offsetValue + 1;
        setPagerOffset(newOffset > 0 ? newOffset : 0);
        if (condition) {
          setSelected({
            key,
            inductee: inducteeFilter[key]
          });
        }
      }
    });

    return () => {
      subscribe.remove();
    };
  }, []);

  const [mobilePager, updateMobilePager] = useState<{
    active: dropdownItem;
    length: number;
    remaining: number;
  }>({
    active: pagerOptions[0],
    length: parseInt(pagerOptions[0].label),
    remaining: 0
  });

  const mobilePagerHandler = (dd: dropdownItem) => {
    const length = parseInt(dd.label);
    const remaining =
      inducteeFilter.length - length > parseInt(dd.label)
        ? parseInt(dd.label)
        : inducteeFilter.length - length;
    updateMobilePager({ ...mobilePager, active: dd, length, remaining });
  };

  const moreBtnHandler = () => {
    const length = mobilePager.length + parseInt(mobilePager.active.label);
    const remaining =
      inducteeFilter.length - length > parseInt(mobilePager.active.label)
        ? parseInt(mobilePager.active.label)
        : inducteeFilter.length - length;
    updateMobilePager({ ...mobilePager, length, remaining });
  };

  const inducteeSelectorHandler = (key: number) => {
    const flag = !toggleExpand || selected.key != key;
    Module.current &&
      Module.current.setAttribute(
        "expanded",
        `${!!(!toggleExpand || selected.key != key)}`
      );
    setSelected({
      key,
      inductee: inducteeFilter[key]
    });
    if (selected.key == 0 && toggleExpand) {
      setPagerOffset(0);
    }
    updateToggleExpand(flag);
  };

  const [ModulePos, setModuleScrollPos] = useState({ posValue: 0 });

  useEffect(() => {
    if (Module.current) {
      const padding = getMediaDevice() == "DESKTOPXL" ? 60 : 90;
      const breadcrumbHeight = 45;
      setModuleScrollPos({
        posValue:
          Math.floor(Module.current.getBoundingClientRect().top) -
          (Module.current.offsetHeight - padding) -
          breadcrumbHeight
      });
    }
  }, []);

  useEffect(() => {
    // waiting for syncronous expanding animation of player card
    const titleToggleOffset = getMediaDevice() == "DESKTOPXL" ? 160 : 140;
    if (moduleLoad) {
      setTimeout(
        () => {
          const moduleScrollOffset = toggleExpand
            ? ModulePos.posValue + titleToggleOffset
            : ModulePos.posValue;
          if (expandedCardRef.current && getMediaDevice() !== "MOBILE") {
            scrollTo({
              top: expandedCardRef.current.offsetTop + moduleScrollOffset
            });
          }
        },
        toggleExpand ? 500 : 0
      );
    }
  }, [selected]);

  return (
    <div className={`hall-of-fame`} ref={Module}>
      <section className="page-section">
        <div className="page-section__inner">
          {title ? <h2>{title}</h2> : null}
          <div className="hall-of-fame__toggle">
            <Toogle
              toggles={toggles}
              onMouseUp={mouseUpHandler}
              updateToggle={updateToggleHandler}
            />
            <div className="hall-of-fame__search">
              <input
                placeholder="Name or Induction Year"
                onChange={searchFieldOnChangeHandler}
              />
            </div>
          </div>
          <div
            className={`hall-of-fame__pager${
              !inducteeFilter.length ? " no-results" : ""
            }${
              (!toggleExpand || selected.key == 0) && !pagerOffset
                ? " hide-prev"
                : ""
            }${
              selected.key == inducteeFilter.length - 1 && toggleExpand
                ? " hide-next"
                : ""
            }${toggleExpand ? " expand" : " collapse"}`}
          >
            <Pager
              data={{ selected, inducteeFilter, pagerOffset }}
              hidePager={!toggleExpand}
              fadedEdge={true}
              tilePadding={22}
              triggerUpdate={inducteeFilter}
              customHookFlag={selected.key}
              customHook={pagerHookHandler}
              tileClassName={"hall-of-fame__inductee"}
              tileWidth={300}
            >
              <div
                className={`desktop hall-of-fame__inductees ${activeToggle}`}
              >
                {inducteeFilter.length ? (
                  inducteeFilter.map((person, key) => (
                    <div
                      key={key}
                      onClick={inducteeSelectorHandler.bind(null, key)}
                      className={`hall-of-fame__inductee-${key} hall-of-fame__inductee-wrapper${
                        selected.key == key && toggleExpand ? " selected" : ""
                      }`}
                    >
                      <Inductee {...person} />
                    </div>
                  ))
                ) : !moduleLoad ? (
                  <Loading />
                ) : (
                  <p className="hall-of-fame__no-result-text">No results</p>
                )}
              </div>
            </Pager>
          </div>
          <div className={`mobile hall-of-fame__inductees ${activeToggle}`}>
            {inducteeFilter.length ? (
              inducteeFilter.slice(0, mobilePager.length).map((person, key) => (
                <div
                  key={key}
                  onClick={inducteeSelectorHandler.bind(null, key)}
                  className={`hall-of-fame__inductee-${key} hall-of-fame__inductee-wrapper${
                    selected.key == key && toggleExpand ? " selected" : ""
                  }`}
                >
                  <Inductee {...person} />
                </div>
              ))
            ) : !moduleLoad ? (
              <Loading />
            ) : (
              <p className="hall-of-fame__no-result-text">No results</p>
            )}
            <div className="hall-of-fame__pager--mobile">
              {inducteeFilter.length > mobilePager.length ? (
                <button
                  className="hall-of-fame__pager-btn"
                  onClick={moreBtnHandler}
                >
                  Show {mobilePager.remaining} more
                </button>
              ) : null}
              {inducteeFilter.length > parseInt(pagerOptions[0].label) ? (
                <MobilePager pagerHandler={mobilePagerHandler} />
              ) : null}
            </div>
          </div>
          <div
            ref={expandedCardRef}
            className={`hall-of-fame__expanded ${
              toggleExpand ? "expand" : "collapse"
            }${
              selected.key == 0 && toggleExpand ? " first" : ""
            } ${activeToggle}`}
          >
            <div className={`hall-of-fame__expanded-card`}>
              <div className="hall-of-fame__expanded-card-main">
                <div className="hall-of-fame__expanded-card-quote">
                  <p>{selected.inductee.quote}</p>
                </div>
                <div
                  style={{
                    backgroundImage: `url("${selected.inductee.imageCardUrl}")`
                  }}
                  className="hall-of-fame__expanded-card-profile"
                ></div>
                <div className="hall-of-fame__expanded-card-profile-details">
                  <div className="hall-of-fame__expanded-card-profile-nation">
                    <span
                      className={`itf-flags itf-flags--${selected.inductee.nationalityCode}`}
                    />
                  </div>
                  <div className="hall-of-fame__expanded-card-profile-name">
                    <div className="firstname">
                      {selected.inductee.firstName}
                    </div>
                    <div className="lastname">{selected.inductee.lastName}</div>
                  </div>
                  <div className="hall-of-fame__expanded-card-profile-category">
                    {selected.inductee.category}
                  </div>
                  {selected.inductee.articleUrl ? (
                    <a
                      href={selected.inductee.articleUrl}
                      target="_blank"
                      className="hall-of-fame__expanded-card-profile__article-link"
                    >
                      See Article
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="hall-of-fame__expanded-card-footer">
                <p>Achievements</p>
                <ul className={`grid-${selected.inductee.achievements.length}`}>
                  {selected.inductee != undefined &&
                  selected.inductee.achievements != undefined
                    ? selected.inductee.achievements.map((achieved, key) => (
                        <li className={`achievements-${key}`} key={key}>
                          <div className="hall-of-fame__expanded-card-footer__value">
                            {achieved.value}
                          </div>
                          <div className="hall-of-fame__expanded-card-footer__label">
                            {achieved.label}
                          </div>
                        </li>
                      ))
                    : null}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HallOfFame;
